
import { computed, defineComponent, reactive, watch } from 'vue'
import DatePicker from '@/components/DatePicker/DatePicker.vue'
import Checkbox from '@/components/Checkbox.vue'
import dayjs from 'dayjs'
import useTabs from '@/composables/useTabs'

interface TabData {
  tab?: HTMLButtonElement;
  min?: string;
  max?: string;
  value?: string;
}

export default defineComponent({
  components: { DatePicker, Checkbox },
  emits: ['update:oneWay', 'update:outward', 'update:return'],
  props: {
    oneWay: Boolean,
    outward: String,
    return: String,
    max: {
      type: String,
      validator: (value: string) => dayjs(value).isBefore(dayjs().add(180, 'days'))
    },
    min: {
      type: String,
      validator: (value: string) => !dayjs(value).isBefore(dayjs())
    }
  },
  // eslint-disable-next-line complexity
  setup (props, { emit }) {
    const today = dayjs()
    const limit = dayjs().add(180, 'day')

    const minDate = computed((): string => {
      const minProp = dayjs(props.min)
      return (minProp < today ? today : minProp).format('YYYY-MM-DD')
    })

    const maxDate = computed((): string => {
      const maxProp = dayjs(props.max || limit)
      return (maxProp > limit ? limit : maxProp).format('YYYY-MM-DD')
    })

    const legs = reactive<{ [x: string]: TabData }>({
      outward: {
        value: props.outward,
        min: minDate.value,
        max: maxDate.value
      },
      return: {
        value: props.return,
        min: props.outward || minDate.value,
        max: maxDate.value
      }
    })
    const tabNames = computed(() => Object.keys(legs))
    const { activeTab, nextTab, previousTab } = computed(() =>
      useTabs(tabNames.value)
    ).value

    const filteredLegs = computed(() => props.oneWay ? { outward: legs.outward } : legs)

    watch(activeTab, newValue => {
      legs[newValue].tab?.focus()
    })

    watch(
      () => legs.return?.value,
      value => {
        legs.outward.max = value || maxDate.value
      }
    )

    watch(
      () => props.oneWay,
      isOneWay => {
        legs.outward.max = isOneWay ? maxDate.value : legs.return?.value || maxDate.value
      }
    )

    watch(
      () => legs.outward.value,
      // eslint-disable-next-line complexity
      value => {
        if (!legs.return) return
        if (value && legs.return.value && value > legs.return.value) {
          legs.return.value = undefined
          emit('update:return', legs.return.value)
        }
        legs.return.min = value || minDate.value
      }
    )

    return {
      activeTab,
      nextTab,
      previousTab,
      filteredLegs,
      formatDate: (date: string) => dayjs(date).format('DD/MM/YYYY'),
      localOneWay: props.oneWay
    }
  }
})
